import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  LANGUAGE_CODE_EN_US,
  LANGUAGE_CODE_ES_US,
  LanguageCodes,
} from 'global/constants';
import { NAMESPACE_TRANSLATION } from 'internationalise';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { useGetStartupData } from 'approot/shared/graphql/startup-data/startup-data.hooks';
import { DeleteExampleConfirm } from './vocabulary-setting-shared';
import Tabs from 'approot/shared/tabs/tabs';
import AutoResizingTextArea from 'approot/shared/auto-resizing-textarea/auto-resizing-textarea';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { FlexColumn, FlexSpaceBetween } from 'approot/shared/styles/flex.style';
import { MoreInfo } from '../../shared/more-information/more-information-toolip';
import ErrorMessageStyled from 'approot/shared/error-message/error-message.style';
import {
  SecondaryButton,
  SecondaryButtonAndIcon,
  SecondaryButtonIcon,
} from 'approot/shared/styles/buttons/secondary-button.style';
import customNanoId from 'lib/nanoid/nanoid';
import {
  FormDefinition,
  FormExample,
  FormWordsTranslation,
} from './vocabulary-setting.constants';
import {
  getTabOptions,
  removeExampleById,
  validateHasValue,
} from './vocabulary-setting-form.utils';
import { HrStyled } from 'approot/shared/styles/decorative.style';
import Select from 'lib/form/select/select-new';
import VocabularySettingImage from './vocabulary-setting-image';
import * as Styled from './vocabulary-setting-form.style';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { getLanguageLabel } from './vocabulary-setting.utils';
import { useRegionData } from 'approot/shared/region/region.apollo';
import { useVocabularySettingContext } from './vocabulary-setting.context';
import { Box } from 'approot/shared/design-system/box/box.style';
import { DropdownKebab } from 'approot/shared/dropdown/dropdown-kebab';
import { DropdownItem } from 'approot/shared/dropdown/dropdown-item.style';
import IconTrash from 'approot/shared/icons/icon-trash';
import IconPlusInCircle from 'approot/shared/icons/icon-plus-in-circle';

const LEXICAL_OPTIONS = [
  {
    text: 'Noun',
    value: 'noun',
  },
  {
    text: 'Pronoun',
    value: 'pronoun',
  },
  {
    text: 'Verb',
    value: 'verb',
  },
  {
    text: 'Adjective',
    value: 'adjective',
  },
  {
    text: 'Adverb',
    value: 'adverb',
  },
];

const MAX_LENGTH = 150;

export const VocabularySettingForm = ({
  isLoading,
  isLoadingLanguage,
  isSavedAndApproved,
  hasTranslateError,
  wordsTranslation,
  definitions,
  examples,
  word,
  isManualAdd,
  lexicalCategory,
  shouldGenerateImages,
  updateWordsTranslation,
  updateDefinitions,
  updateExamples,
  updateLexical,
  handleAddTranslation,
  handleRemoveLanguage,
}: {
  isLoading: boolean;
  isLoadingLanguage: boolean;
  isSavedAndApproved: boolean;
  hasTranslateError?: boolean;
  wordsTranslation: FormWordsTranslation[];
  definitions: FormDefinition[];
  examples: FormExample[];
  word?: string;
  isManualAdd?: boolean;
  lexicalCategory: string;
  shouldGenerateImages: boolean;
  updateWordsTranslation: (translations: FormWordsTranslation[]) => void;
  updateDefinitions: (definitions: FormDefinition[]) => void;
  updateExamples: (data: FormExample[]) => void;
  updateLexical?: (value: string) => void;
  handleAddTranslation?: (language: LanguageCodes) => void;
  handleRemoveLanguage?: (language: LanguageCodes) => void;
}) => {
  const { t } = useTranslation(NAMESPACE_TRANSLATION);

  const { isRegionUS } = useRegionData();
  const { formErrors, setFormErrors, currentLanguage, setCurrentLanguage } =
    useVocabularySettingContext();

  const activeExampleIndexRef = useRef<number>(undefined);
  const activeExampleIdRef = useRef<string>(undefined);

  const { data } = useGetStartupData();

  const [tabOptions, setTabOptions] = useState<
    { label: string; value: string }[]
  >(getTabOptions(definitions));
  const {
    isVisible: isConfirmDeleteVisible,
    openModal: openConfirmDeleteModal,
    closeModal: closeConfirmDeleteModal,
  } = useSimpleModalState();

  const exampleIndex = activeExampleIndexRef.current;
  const exampleId = activeExampleIdRef.current;

  const addLanguageTranslation = useCallback(
    (language: LanguageCodes) => {
      tabOptions.push({
        label: getLanguageLabel(language),
        value: language,
      });
      setTabOptions([...tabOptions]);
      setCurrentLanguage(language);
      handleAddTranslation?.(language);
    },
    [tabOptions, setCurrentLanguage, setTabOptions, handleAddTranslation]
  );

  const handleUpdateWordsTranslation = useCallback(
    (value: string) => {
      const index = wordsTranslation.findIndex(
        w => w.language === currentLanguage
      );
      wordsTranslation[index].word = value;
      wordsTranslation[index].error = validateHasValue(
        wordsTranslation[index].word
      );
      updateWordsTranslation([...wordsTranslation]);
    },
    [updateWordsTranslation, wordsTranslation, currentLanguage]
  );

  const handleUpdateDefinition = useCallback(
    (value: string, id: string) => {
      const index = definitions.findIndex(d => d.id === id);
      definitions[index].description = value;
      definitions[index].error = validateHasValue(
        definitions[index].description
      );
      updateDefinitions([...definitions]);
    },
    [definitions, updateDefinitions]
  );

  const handleUpdateExample = useCallback(
    (example: FormExample) => {
      const index = examples.findIndex(
        e =>
          e.exampleId === example.exampleId && e.language === example.language
      );
      examples[index] = example;
      updateExamples([...examples]);
    },
    [examples, updateExamples]
  );

  const handleRemoveExample = useCallback(
    (id: string) => {
      // remove the example matching this id from all languages
      const updatedExamples = removeExampleById(examples, id);
      updateExamples([...updatedExamples]);
    },
    [examples, updateExamples]
  );

  const languageData = useMemo(() => {
    const data = [
      {
        language: LANGUAGE_CODE_EN_US,
        definitions: definitions.filter(
          i => i.language === LANGUAGE_CODE_EN_US
        ),
        examples: examples.filter(i => i.language === LANGUAGE_CODE_EN_US),
        wordsTranslation: wordsTranslation.find(
          i => i.language === LANGUAGE_CODE_EN_US
        ) || { language: LANGUAGE_CODE_EN_US, word: word || '' },
      },
    ];

    if (definitions.some(d => d.language !== LANGUAGE_CODE_EN_US)) {
      data.push({
        language: LANGUAGE_CODE_ES_US,
        definitions: definitions.filter(
          i => i.language === LANGUAGE_CODE_ES_US
        ),
        examples: examples.filter(i => i.language === LANGUAGE_CODE_ES_US),
        wordsTranslation: wordsTranslation.find(
          i => i.language === LANGUAGE_CODE_ES_US
        ) || { language: LANGUAGE_CODE_ES_US, word: '' },
      });
    }
    return data;
  }, [definitions, examples, word, wordsTranslation]);

  const currentLanguageData = useMemo(() => {
    return languageData.find(ld => ld.language === currentLanguage);
  }, [currentLanguage, languageData]);

  const allLanguages = useMemo(() => {
    return languageData.map(ld => ld.language as LanguageCodes);
  }, [languageData]);

  useEffect(() => {
    if (formErrors?.time) {
      const currentTabError = formErrors.fields.find(
        f => f.language === currentLanguage
      );
      if (currentTabError) {
        document.getElementById(currentTabError.id)?.focus();
      } else {
        setCurrentLanguage(formErrors.fields[0].language);
        setTimeout(
          () => document.getElementById(formErrors.fields[0].id)?.focus(),
          0
        );
      }
    }
    return () => {
      setFormErrors(undefined);
    };
  }, [formErrors, currentLanguage, setCurrentLanguage, setFormErrors]);

  useEffect(() => {
    if (hasTranslateError) {
      setCurrentLanguage(LANGUAGE_CODE_EN_US);
      setTabOptions([{ label: 'English', value: LANGUAGE_CODE_EN_US }]);
    }
  }, [hasTranslateError, setCurrentLanguage, setTabOptions]);

  useEffect(() => {
    // Reset the tab options when the definitions change
    setTabOptions(getTabOptions(definitions));
  }, [definitions]);

  useEffect(() => {
    // Reset to English when the form is closed
    return () => {
      setCurrentLanguage(LANGUAGE_CODE_EN_US);
    };
  }, [setCurrentLanguage]);

  if (isLoading) {
    return <Bouncer />;
  }

  return (
    <>
      {isConfirmDeleteVisible && exampleId !== undefined && (
        <DeleteExampleConfirm
          index={exampleIndex || 0}
          onConfirm={() => {
            if (exampleId !== undefined) {
              handleRemoveExample(exampleId);
              closeConfirmDeleteModal();
            }
          }}
          onClose={closeConfirmDeleteModal}
        />
      )}

      <SettingsGroup>
        <SettingsLabel htmlFor="lexical">
          Lexical Category
          {!isManualAdd && (
            <MoreInfo>
              Lexical category can't be edited, please return to select target
              version of the word or create from scratch
            </MoreInfo>
          )}
        </SettingsLabel>
        <Select
          id="lexical"
          name="lexical"
          dataSelector="lexical"
          disabled={!isManualAdd}
          defaultValue={lexicalCategory}
          value={LEXICAL_OPTIONS[0].value}
          options={LEXICAL_OPTIONS}
          onChange={e => updateLexical?.(e.target.value)}
        />
      </SettingsGroup>

      <Styled.VocabularyTabs>
        <Tabs
          options={tabOptions}
          activeTab={currentLanguage}
          onSelect={option => setCurrentLanguage(option.value as LanguageCodes)}
          ariaDescription="tabs"
          contentPanelId="content"
        />
        {isRegionUS && (
          <>
            {!isSavedAndApproved ? (
              <TooltipWrapper
                multiline
                hoverText="Word must be Saved and Approved before adding Spanish"
              >
                <SecondaryButtonAndIcon
                  type="button"
                  data-selector="btn-add-spanish"
                  $isSmall
                  disabled
                >
                  Add Spanish
                  <IconPlusInCircle width={16} height={16} />
                </SecondaryButtonAndIcon>
              </TooltipWrapper>
            ) : !isLoadingLanguage &&
              languageData.every(l => l.language !== LANGUAGE_CODE_ES_US) ? (
              <SecondaryButtonAndIcon
                data-selector="btn-add-spanish"
                type="button"
                $isSmall
                onClick={() => addLanguageTranslation(LANGUAGE_CODE_ES_US)}
              >
                Add Spanish
                <IconPlusInCircle width={16} height={16} />
              </SecondaryButtonAndIcon>
            ) : (
              !isLoadingLanguage && (
                <DropdownKebab
                  dropdownContents={allLanguages.map(
                    language =>
                      language !== LANGUAGE_CODE_EN_US && (
                        <DropdownItem
                          key={language}
                          $isNegative
                          onClick={() => handleRemoveLanguage?.(language)}
                        >
                          {`Remove ${getLanguageLabel(language)}`}
                        </DropdownItem>
                      )
                  )}
                  aria-label="Language Options"
                />
              )
            )}
          </>
        )}
      </Styled.VocabularyTabs>
      <div id="content">
        {currentLanguageData && currentLanguage === LANGUAGE_CODE_EN_US ? (
          <>
            <DefinitionField
              definition={currentLanguageData.definitions[0]}
              label={`${t('content.year_0_short')} - 2 Definition`}
              updateDefinition={value =>
                handleUpdateDefinition(
                  value,
                  currentLanguageData.definitions[0].id
                )
              }
            />
            <DefinitionField
              definition={currentLanguageData.definitions[1]}
              label={`${
                '3 - ' + data?.startupData.years.at(-1)?.name
              } Definition`}
              updateDefinition={value =>
                handleUpdateDefinition(
                  value,
                  currentLanguageData.definitions[1].id
                )
              }
            />

            <HrStyled $marginBottom={24} />

            {currentLanguageData.examples.map((item, index) => (
              <ExampleField
                key={item.exampleId}
                example={item}
                index={index}
                isViewOnly={false}
                shouldGenerateImage={
                  !currentLanguageData.examples[index].image &&
                  shouldGenerateImages
                }
                word={word || ''}
                language={currentLanguage}
                onSetImage={example => {
                  example.skipGeneratingImage = true;
                  example.isGeneratingImage = false;
                  handleUpdateExample(example);
                }}
                onChangeText={e => {
                  item.example = e.target.value;
                  item.error = validateHasValue(item.example);
                  handleUpdateExample(item);
                }}
                onDelete={() => {
                  activeExampleIndexRef.current = index;
                  activeExampleIdRef.current = item.exampleId;
                  openConfirmDeleteModal();
                }}
              />
            ))}
          </>
        ) : (
          <>
            {isLoadingLanguage ? (
              <FlexColumn $marginTop={32}>
                <Bouncer includeScreenReaderProps />
                <Box $marginTop={16}>Un momento, por favor</Box>
              </FlexColumn>
            ) : (
              currentLanguageData &&
              currentLanguageData.definitions.length > 0 && (
                <OtherLanguageForm
                  word={word?.trim() || ''}
                  language={currentLanguage}
                  wordsTranslation={currentLanguageData?.wordsTranslation}
                  definitions={currentLanguageData?.definitions || []}
                  examples={currentLanguageData?.examples || []}
                  updateDefinition={handleUpdateDefinition}
                  updateExamples={handleUpdateExample}
                  updateWordsTranslation={handleUpdateWordsTranslation}
                />
              )
            )}
          </>
        )}

        {!isLoadingLanguage && (
          <>
            {currentLanguage === LANGUAGE_CODE_EN_US ? (
              <SecondaryButton
                type="button"
                onClick={() => {
                  examples.push({
                    exampleId: customNanoId(),
                    example: '',
                    language: LANGUAGE_CODE_EN_US,
                    skipGeneratingImage: true,
                    image: undefined,
                  });
                  updateExamples([...examples]);
                }}
                data-selector="btn-another-example"
              >
                Add Another Example
              </SecondaryButton>
            ) : (
              <TooltipWrapper hoverText="Only English examples can be added or removed">
                <SecondaryButton
                  type="button"
                  data-selector="btn-another-example"
                  disabled
                >
                  Add Another Example
                </SecondaryButton>
              </TooltipWrapper>
            )}
          </>
        )}
      </div>
    </>
  );
};

const WordsTranslationField = ({
  word,
  wordsTranslation,
  language,
  updateWordsTranslation,
}: {
  word: string;
  wordsTranslation: FormWordsTranslation;
  language: LanguageCodes;
  updateWordsTranslation: (value: string) => void;
}) => {
  return (
    <SettingsGroup>
      <SettingsLabel htmlFor={word}>
        {`'${word}' in ${getLanguageLabel(language)}`}
      </SettingsLabel>
      <AutoResizingTextArea
        maxLength={MAX_LENGTH}
        id={word}
        name={word}
        initialValue={wordsTranslation.word}
        hasError={!!wordsTranslation.error}
        onChange={e => updateWordsTranslation(e.target.value)}
        aria-invalid={!!wordsTranslation.error}
        aria-describedby={`translated-word-${word}-error`}
      />
      {wordsTranslation.error && (
        <ErrorMessageStyled id={`translated-word-${word}-error`}>
          {wordsTranslation.error}
        </ErrorMessageStyled>
      )}
    </SettingsGroup>
  );
};

const DefinitionField = ({
  definition,
  label,
  updateDefinition,
}: {
  definition: FormDefinition;
  label: string;
  updateDefinition: (value: string) => void;
}) => {
  return (
    <SettingsGroup marginBottom={8}>
      <SettingsLabel htmlFor={definition.id}>{label}</SettingsLabel>
      <AutoResizingTextArea
        showCounter
        maxLength={MAX_LENGTH}
        id={definition.id}
        initialValue={definition.description}
        name={definition.id}
        hasError={!!definition.error}
        aria-invalid={!!definition.error}
        aria-describedby={`definition-${definition.id}-error`}
        onChange={e => updateDefinition(e.target.value)}
        data-selector="txt-definition"
      />
      {definition.error && (
        <ErrorMessageStyled
          id={`definition-${definition.id}-error`}
          data-selector="error-definition"
        >
          {definition.error}
        </ErrorMessageStyled>
      )}
    </SettingsGroup>
  );
};

const ExampleField = ({
  example,
  index,
  word,
  isViewOnly,
  shouldGenerateImage,
  language,
  onSetImage,
  onChangeText,
  onDelete,
}: {
  example: FormExample;
  index: number;
  word: string;
  language: LanguageCodes;
  isViewOnly?: boolean;
  shouldGenerateImage: boolean;
  onSetImage: (example: FormExample) => void;
  onChangeText: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onDelete?: () => void;
}) => {
  return (
    <SettingsGroup key={example.exampleId} marginBottom={16}>
      <FlexSpaceBetween $marginBottom={16}>
        <SettingsLabel htmlFor={example.exampleId}>
          Example {index + 1}
          <MoreInfo>
            Write a simple sentence using {word}, to show how it is used and
            what it means.
          </MoreInfo>
        </SettingsLabel>
        {isViewOnly ? (
          <TooltipWrapper hoverText="Only English examples can be added or removed">
            <SecondaryButtonIcon
              type="button"
              $isSmall
              disabled
              aria-label="Only English examples can be added or removed"
            >
              <IconTrash width={20} height={20} />
            </SecondaryButtonIcon>
          </TooltipWrapper>
        ) : (
          <SecondaryButtonIcon
            type="button"
            $isSmall
            aria-label={`Delete example ${index + 1}: ${example.example}`}
            disabled={example.isGeneratingImage}
            onClick={onDelete}
            data-selector={`delete-example-${index + 1}`}
          >
            <IconTrash width={20} height={20} />
          </SecondaryButtonIcon>
        )}
      </FlexSpaceBetween>

      <Styled.VocabularyDefinitionExample>
        <VocabularySettingImage
          index={index}
          item={example}
          word={word}
          language={language}
          canQuery={shouldGenerateImage}
          isViewOnly={isViewOnly}
          onSetImage={onSetImage}
        />
        <div>
          <AutoResizingTextArea
            showCounter
            disabled={example.isGeneratingImage}
            maxLength={MAX_LENGTH}
            id={example.exampleId}
            name={example.exampleId}
            initialValue={example.example}
            hasError={!!example.error}
            aria-invalid={!!example.error}
            aria-describedby={`error-${example.exampleId}`}
            onChange={onChangeText}
            data-selector={`txt-example-${index + 1}`}
          />
          {example.error && (
            <ErrorMessageStyled
              id={`error-${example.exampleId}`}
              data-selector={`error-example-${index + 1}`}
            >
              {example.error}
            </ErrorMessageStyled>
          )}
        </div>
      </Styled.VocabularyDefinitionExample>
    </SettingsGroup>
  );
};

const OtherLanguageForm = ({
  language,
  word,
  wordsTranslation,
  definitions,
  examples,
  updateDefinition,
  updateExamples,
  updateWordsTranslation,
}: {
  language: LanguageCodes;
  word: string;
  wordsTranslation: FormWordsTranslation;
  definitions: FormDefinition[];
  examples: FormExample[];
  updateDefinition: (value: string, id: string) => void;
  updateExamples: (example: FormExample) => void;
  updateWordsTranslation: (value: string) => void;
}) => {
  const { t } = useTranslation(NAMESPACE_TRANSLATION);
  const { data } = useGetStartupData();

  return (
    <div>
      <WordsTranslationField
        word={word}
        wordsTranslation={wordsTranslation}
        language={language}
        updateWordsTranslation={value => updateWordsTranslation(value)}
      />
      <DefinitionField
        definition={definitions[0]}
        label={`${t('content.year_0_short')} - 2 Definition`}
        updateDefinition={value => updateDefinition(value, definitions[0].id)}
      />
      <DefinitionField
        definition={definitions[1]}
        label={`${'3 - ' + data?.startupData.years.at(-1)?.name} Definition`}
        updateDefinition={value => updateDefinition(value, definitions[1].id)}
      />
      <HrStyled $marginBottom={24} />
      {examples.map((item, index) => (
        <ExampleField
          key={item.exampleId}
          example={item}
          index={index}
          word={word}
          language={language}
          isViewOnly
          shouldGenerateImage={false}
          onSetImage={example => {
            example.isGeneratingImage = false;
            updateExamples(example);
          }}
          onChangeText={e => {
            item.example = e.target.value;
            item.error = validateHasValue(item.example);
            updateExamples(item);
          }}
        />
      ))}
    </div>
  );
};
